import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';
import { motion } from 'framer-motion';
import {
  Check,
  Crop,
  Download,
  FileImage,
  Loader,
  PenTool,
  Upload,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ToolLayout } from '@layouts/ToolLayout';

import { Button } from '@ui/Button';
import Toggle from '@ui/Toggle';

import { COLORS } from '@constants/colors';
import { CONTACT_INFO } from '@constants/contact';

// Document Cropper Modal Component with Perspective Correction
const DocumentCropperModal: React.FC<{
  tempDocumentForCrop: string | null;
  perspectiveCorners: { x: number; y: number }[];
  setPerspectiveCorners: (corners: { x: number; y: number }[]) => void;
  draggingCorner: number | null;
  setDraggingCorner: (corner: number | null) => void;
  documentPerspectiveImageRef: React.RefObject<HTMLImageElement>;
  documentPerspectiveCanvasRef: React.RefObject<HTMLCanvasElement>;
  initializePerspectiveCorners: (width: number, height: number) => void;
  handleCancelDocumentCrop: () => void;
  handleDocumentCropComplete: () => void;
}> = ({
  tempDocumentForCrop,
  perspectiveCorners,
  setPerspectiveCorners,
  draggingCorner,
  setDraggingCorner,
  documentPerspectiveImageRef,
  documentPerspectiveCanvasRef,
  handleCancelDocumentCrop,
  handleDocumentCropComplete,
  initializePerspectiveCorners,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  // Draw perspective canvas
  const drawPerspectiveCanvas = useCallback(() => {
    const canvas = documentPerspectiveCanvasRef.current;
    const img = documentPerspectiveImageRef.current;
    if (!canvas || !img || !imageLoaded) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;

    // Draw image
    ctx.drawImage(img, 0, 0);

    // Draw perspective overlay
    if (perspectiveCorners.length === 4) {
      ctx.save();

      // Draw semi-transparent overlay everywhere except inside the quad
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Clear the quad area
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.moveTo(perspectiveCorners[0].x, perspectiveCorners[0].y);
      for (let i = 1; i < 4; i++) {
        ctx.lineTo(perspectiveCorners[i].x, perspectiveCorners[i].y);
      }
      ctx.closePath();
      ctx.fill();

      ctx.globalCompositeOperation = 'source-over';

      // Draw border
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(perspectiveCorners[0].x, perspectiveCorners[0].y);
      for (let i = 1; i < 4; i++) {
        ctx.lineTo(perspectiveCorners[i].x, perspectiveCorners[i].y);
      }
      ctx.closePath();
      ctx.stroke();

      // Draw corner handles
      perspectiveCorners.forEach((corner, index) => {
        ctx.beginPath();
        ctx.arc(corner.x, corner.y, 15, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 3;
        ctx.stroke();

        // Draw corner label
        ctx.fillStyle = '#3b82f6';
        ctx.font = 'bold 12px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const labels = ['TL', 'TR', 'BR', 'BL'];
        ctx.fillText(labels[index], corner.x, corner.y);
      });

      ctx.restore();
    }
  }, [
    documentPerspectiveCanvasRef,
    documentPerspectiveImageRef,
    imageLoaded,
    perspectiveCorners,
  ]);

  useEffect(() => {
    drawPerspectiveCanvas();
  }, [drawPerspectiveCanvas]);

  // Handle image load
  const handleImageLoad = () => {
    const img = documentPerspectiveImageRef.current;
    if (img && perspectiveCorners.length === 0) {
      initializePerspectiveCorners(img.naturalWidth, img.naturalHeight);
      setImageLoaded(true);
    }
  };

  // Mouse handlers for perspective mode
  const getCanvasCoords = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = documentPerspectiveCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = getCanvasCoords(e);

    // Find which corner was clicked (check closest first)
    let closestIndex = -1;
    let closestDist = Infinity;

    perspectiveCorners.forEach((corner, index) => {
      const dist = Math.sqrt(
        Math.pow(pos.x - corner.x, 2) + Math.pow(pos.y - corner.y, 2)
      );
      if (dist < 30 && dist < closestDist) {
        closestDist = dist;
        closestIndex = index;
      }
    });

    if (closestIndex !== -1) {
      setDraggingCorner(closestIndex);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (draggingCorner === null) return;
    const pos = getCanvasCoords(e);

    const newCorners = [...perspectiveCorners];
    newCorners[draggingCorner] = pos;
    setPerspectiveCorners(newCorners);
  };

  const handleMouseUp = () => {
    setDraggingCorner(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
      <motion.div
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Crop size={24} style={{ color: COLORS.sage }} />
              <h3
                className="text-xl font-bold ml-3"
                style={{ color: COLORS.primary }}
              >
                Adjust document corners
              </h3>
            </div>
            <button
              onClick={handleCancelDocumentCrop}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6">
          <div className="relative">
            <img
              ref={documentPerspectiveImageRef}
              src={tempDocumentForCrop || ''}
              alt="Document"
              style={{ display: 'none' }}
              onLoad={handleImageLoad}
            />
            <canvas
              ref={documentPerspectiveCanvasRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              style={{
                maxWidth: '100%',
                display: 'block',
                cursor: draggingCorner !== null ? 'grabbing' : 'grab',
              }}
            />
            <div className="mt-4 text-sm text-gray-600">
              <p className="mb-1">
                Drag each corner to match the document edges. All corners can
                move independently.
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex gap-3">
          <Button onClick={handleCancelDocumentCrop} variant="ghost" fullWidth>
            Cancel
          </Button>
          <Button
            onClick={handleDocumentCropComplete}
            variant="accent"
            fullWidth
            icon={Check}
            iconSize={20}
          >
            Apply
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export const SignaturePlacer = () => {
  const [signatureImage, setSignatureImage] = useState<HTMLImageElement | null>(
    null
  );
  const [documentImage, setDocumentImage] = useState<HTMLImageElement | null>(
    null
  );
  const [removeWhiteBg, setRemoveWhiteBg] = useState(true);
  const [bgThreshold, setBgThreshold] = useState(175);
  const [grayscaleOutput, setGrayscaleOutput] = useState(false);

  // Signature cropper states
  const [showSignatureCropper, setShowSignatureCropper] = useState(false);
  const [tempSignatureForCrop, setTempSignatureForCrop] = useState<
    string | null
  >(null);
  const [signatureCropper, setSignatureCropper] = useState<Cropper | null>(
    null
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');

  // Document cropper states
  const [showDocumentCropper, setShowDocumentCropper] = useState(false);
  const [tempDocumentForCrop, setTempDocumentForCrop] = useState<string | null>(
    null
  );
  const [documentCropper, setDocumentCropper] = useState<Cropper | null>(null);

  // Perspective correction states (always ON)
  const [perspectiveCorners, setPerspectiveCorners] = useState<
    { x: number; y: number }[]
  >([]);
  const [draggingCorner, setDraggingCorner] = useState<number | null>(null);
  const documentPerspectiveCanvasRef = useRef<HTMLCanvasElement>(null);
  const documentPerspectiveImageRef = useRef<HTMLImageElement>(null);

  // Position states (signature transform on document)
  const [signatureTransform, setSignatureTransform] = useState({
    x: 0,
    y: 0,
    width: 300,
    height: 150,
    rotation: 0,
  });

  const signatureCropperImageRef = useRef<HTMLImageElement>(null);
  const signaturePreviewCanvasRef = useRef<HTMLImageElement>(null);
  const documentCropperImageRef = useRef<HTMLImageElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const compositeCanvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputSignatureRef = useRef<HTMLInputElement>(null);
  const fileInputDocumentRef = useRef<HTMLInputElement>(null);

  // Interactive controls state
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [activeHandle, setActiveHandle] = useState<string | null>(null);
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });

  // Update signature preview when threshold or crop changes
  const updateSignaturePreview = useCallback(() => {
    if (
      !signatureCropper ||
      !removeWhiteBg ||
      !signaturePreviewCanvasRef.current
    )
      return;

    const croppedCanvas = signatureCropper.getCroppedCanvas({
      maxWidth: 800,
      maxHeight: 800,
    });

    if (!croppedCanvas) return;

    const previewCanvas = document.createElement('canvas');
    previewCanvas.width = croppedCanvas.width;
    previewCanvas.height = croppedCanvas.height;

    const ctx = previewCanvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(croppedCanvas, 0, 0);

    // Apply background removal
    const imageData = ctx.getImageData(
      0,
      0,
      previewCanvas.width,
      previewCanvas.height
    );
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      if (r > bgThreshold && g > bgThreshold && b > bgThreshold) {
        data[i + 3] = 0;
      }
    }

    ctx.putImageData(imageData, 0, 0);
    signaturePreviewCanvasRef.current.src =
      previewCanvas.toDataURL('image/png');
  }, [signatureCropper, removeWhiteBg, bgThreshold]);

  useEffect(() => {
    updateSignaturePreview();
  }, [updateSignaturePreview]);

  // Listen to signature cropper events for live preview
  useEffect(() => {
    if (
      !signatureCropper ||
      !removeWhiteBg ||
      !signatureCropperImageRef.current
    )
      return;

    const handleCropMove = () => {
      updateSignaturePreview();
    };

    const imageElement = signatureCropperImageRef.current;
    imageElement.addEventListener('crop', handleCropMove);

    return () => {
      imageElement.removeEventListener('crop', handleCropMove);
    };
  }, [signatureCropper, removeWhiteBg, updateSignaturePreview]);

  const handleSignatureUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = e => {
        const dataUrl = e.target?.result as string;
        setTempSignatureForCrop(dataUrl);
        setShowSignatureCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = e => {
        const dataUrl = e.target?.result as string;
        setTempDocumentForCrop(dataUrl);
        setShowDocumentCropper(true);
        setPerspectiveCorners([]);
      };
      reader.readAsDataURL(file);
    }
  };

  // Initialize perspective corners based on image size
  const initializePerspectiveCorners = (width: number, height: number) => {
    const margin = Math.min(width, height) * 0.1;
    setPerspectiveCorners([
      { x: margin, y: margin }, // top-left
      { x: width - margin, y: margin }, // top-right
      { x: width - margin, y: height - margin }, // bottom-right
      { x: margin, y: height - margin }, // bottom-left
    ]);
  };

  // Calculate perspective transform matrix (homography)
  // We need the INVERSE transform: from destination (rectangle) to source (quad)
  const getPerspectiveTransform = (
    srcPoints: { x: number; y: number }[],
    dstWidth: number,
    dstHeight: number
  ) => {
    // Destination points (perfect rectangle)
    const dst = [
      { x: 0, y: 0 },
      { x: dstWidth, y: 0 },
      { x: dstWidth, y: dstHeight },
      { x: 0, y: dstHeight },
    ];

    // Build coefficient matrix for INVERSE homography (dst -> src)
    // We swap src and dst to get the inverse transform
    const A: number[][] = [];
    for (let i = 0; i < 4; i++) {
      const d = dst[i];
      const src = srcPoints[i];
      A.push([d.x, d.y, 1, 0, 0, 0, -src.x * d.x, -src.x * d.y]);
      A.push([0, 0, 0, d.x, d.y, 1, -src.y * d.x, -src.y * d.y]);
    }

    const b = [
      srcPoints[0].x,
      srcPoints[0].y,
      srcPoints[1].x,
      srcPoints[1].y,
      srcPoints[2].x,
      srcPoints[2].y,
      srcPoints[3].x,
      srcPoints[3].y,
    ];

    // Solve using Gaussian elimination (simplified)
    return solveLinearSystem(A, b);
  };

  // Simple linear system solver
  const solveLinearSystem = (A: number[][], b: number[]): number[] => {
    const n = A.length;
    // Gaussian elimination with partial pivoting
    for (let i = 0; i < n; i++) {
      // Find pivot
      let maxRow = i;
      for (let k = i + 1; k < n; k++) {
        if (Math.abs(A[k][i]) > Math.abs(A[maxRow][i])) {
          maxRow = k;
        }
      }
      [A[i], A[maxRow]] = [A[maxRow], A[i]];
      [b[i], b[maxRow]] = [b[maxRow], b[i]];

      // Make all rows below this one 0 in current column
      for (let k = i + 1; k < n; k++) {
        const c = A[k][i] / A[i][i];
        for (let j = i; j < n; j++) {
          if (i === j) {
            A[k][j] = 0;
          } else {
            A[k][j] -= c * A[i][j];
          }
        }
        b[k] -= c * b[i];
      }
    }

    // Back substitution
    const x = new Array(n).fill(0);
    for (let i = n - 1; i >= 0; i--) {
      x[i] = b[i];
      for (let j = i + 1; j < n; j++) {
        x[i] -= A[i][j] * x[j];
      }
      x[i] /= A[i][i];
    }

    return [...x, 1]; // Append 1 for homogeneous coordinates
  };

  // Apply perspective transform to create corrected image
  const applyPerspectiveTransform = (
    img: HTMLImageElement,
    corners: { x: number; y: number }[]
  ): HTMLCanvasElement => {
    // Use natural dimensions
    const imgWidth = img.naturalWidth;
    const imgHeight = img.naturalHeight;

    console.log('Transform input:', { imgWidth, imgHeight, corners });

    // Calculate source dimensions to determine orientation
    const width1 = Math.sqrt(
      Math.pow(corners[1].x - corners[0].x, 2) +
        Math.pow(corners[1].y - corners[0].y, 2)
    );
    const width2 = Math.sqrt(
      Math.pow(corners[2].x - corners[3].x, 2) +
        Math.pow(corners[2].y - corners[3].y, 2)
    );
    const height1 = Math.sqrt(
      Math.pow(corners[3].x - corners[0].x, 2) +
        Math.pow(corners[3].y - corners[0].y, 2)
    );
    const height2 = Math.sqrt(
      Math.pow(corners[2].x - corners[1].x, 2) +
        Math.pow(corners[2].y - corners[1].y, 2)
    );

    const avgWidth = (width1 + width2) / 2;
    const avgHeight = (height1 + height2) / 2;

    // A4 dimensions at 300 DPI (print quality)
    const A4_WIDTH_300DPI = 2480;
    const A4_HEIGHT_300DPI = 3508;

    // Determine if document is portrait or landscape
    const isPortrait = avgHeight > avgWidth;

    // Set destination to A4 size with correct orientation
    const dstWidth = isPortrait ? A4_WIDTH_300DPI : A4_HEIGHT_300DPI;
    const dstHeight = isPortrait ? A4_HEIGHT_300DPI : A4_WIDTH_300DPI;

    console.log('Destination size (A4):', { dstWidth, dstHeight, isPortrait });

    if (
      dstWidth <= 0 ||
      dstHeight <= 0 ||
      dstWidth > 10000 ||
      dstHeight > 10000
    ) {
      console.error('Invalid destination dimensions');
      throw new Error('Invalid destination dimensions');
    }

    const canvas = document.createElement('canvas');
    canvas.width = dstWidth;
    canvas.height = dstHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas context');

    // Get transformation matrix
    const h = getPerspectiveTransform(corners, dstWidth, dstHeight);
    console.log('Homography matrix:', h);

    // Validate homography
    if (h.some(val => !isFinite(val))) {
      console.error('Invalid homography matrix');
      throw new Error('Invalid homography matrix');
    }

    // Draw source image
    const srcCanvas = document.createElement('canvas');
    srcCanvas.width = imgWidth;
    srcCanvas.height = imgHeight;
    const srcCtx = srcCanvas.getContext('2d');
    if (srcCtx) {
      srcCtx.drawImage(img, 0, 0);
    }
    const srcData = srcCtx?.getImageData(0, 0, imgWidth, imgHeight);
    if (!srcData) throw new Error('Could not get source image data');

    // Create destination image data
    const dstData = ctx.createImageData(dstWidth, dstHeight);

    // Apply inverse perspective transformation
    let pixelsProcessed = 0;
    for (let y = 0; y < dstHeight; y++) {
      for (let x = 0; x < dstWidth; x++) {
        // Map destination pixel to source using inverse transform
        const denominator = h[6] * x + h[7] * y + h[8];
        const srcX = (h[0] * x + h[1] * y + h[2]) / denominator;
        const srcY = (h[3] * x + h[4] * y + h[5]) / denominator;

        // Bilinear interpolation
        if (
          srcX >= 0 &&
          srcX < imgWidth - 1 &&
          srcY >= 0 &&
          srcY < imgHeight - 1
        ) {
          const x0 = Math.floor(srcX);
          const y0 = Math.floor(srcY);
          const x1 = x0 + 1;
          const y1 = y0 + 1;
          const dx = srcX - x0;
          const dy = srcY - y0;

          const dstIdx = (y * dstWidth + x) * 4;

          for (let c = 0; c < 4; c++) {
            const p00 = srcData.data[(y0 * imgWidth + x0) * 4 + c];
            const p10 = srcData.data[(y0 * imgWidth + x1) * 4 + c];
            const p01 = srcData.data[(y1 * imgWidth + x0) * 4 + c];
            const p11 = srcData.data[(y1 * imgWidth + x1) * 4 + c];

            const val =
              p00 * (1 - dx) * (1 - dy) +
              p10 * dx * (1 - dy) +
              p01 * (1 - dx) * dy +
              p11 * dx * dy;

            dstData.data[dstIdx + c] = val;
          }
          pixelsProcessed++;
        }
      }
    }

    console.log(
      'Pixels processed:',
      pixelsProcessed,
      '/',
      dstWidth * dstHeight
    );

    ctx.putImageData(dstData, 0, 0);
    return canvas;
  };

  // Initialize signature cropper
  useEffect(() => {
    if (
      showSignatureCropper &&
      signatureCropperImageRef.current &&
      tempSignatureForCrop
    ) {
      const cropperInstance = new Cropper(signatureCropperImageRef.current, {
        aspectRatio: NaN,
        viewMode: 1,
        autoCropArea: 0.8,
        responsive: true,
        background: false,
        guides: true,
        center: true,
        highlight: true,
        cropBoxMovable: true,
        cropBoxResizable: true,
        toggleDragModeOnDblclick: false,
      });
      setSignatureCropper(cropperInstance);

      return () => {
        cropperInstance.destroy();
      };
    }
  }, [showSignatureCropper, tempSignatureForCrop]);

  // Initialize document cropper
  useEffect(() => {
    if (
      showDocumentCropper &&
      documentCropperImageRef.current &&
      tempDocumentForCrop
    ) {
      const cropperInstance = new Cropper(documentCropperImageRef.current, {
        aspectRatio: NaN,
        viewMode: 1,
        autoCropArea: 0.9,
        responsive: true,
        background: true,
        guides: true,
        center: true,
        highlight: true,
        cropBoxMovable: true,
        cropBoxResizable: true,
        toggleDragModeOnDblclick: false,
      });
      setDocumentCropper(cropperInstance);

      return () => {
        cropperInstance.destroy();
      };
    }
  }, [showDocumentCropper, tempDocumentForCrop]);

  const removeBackgroundCanvas = (
    canvas: HTMLCanvasElement
  ): HTMLCanvasElement => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      if (r > bgThreshold && g > bgThreshold && b > bgThreshold) {
        data[i + 3] = 0;
      }
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas;
  };

  const handleSignatureCropComplete = async () => {
    if (!signatureCropper) return;

    setIsProcessing(true);
    setProcessingStatus('Processing signature...');

    try {
      const croppedCanvas = signatureCropper.getCroppedCanvas({
        maxWidth: 2000,
        maxHeight: 2000,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });

      if (!croppedCanvas) {
        throw new Error('Failed to crop signature');
      }

      let finalCanvas = croppedCanvas;

      if (removeWhiteBg) {
        setProcessingStatus('Removing background...');
        finalCanvas = removeBackgroundCanvas(croppedCanvas);
      }

      const img = new Image();
      img.onload = () => {
        setSignatureImage(img);
        setShowSignatureCropper(false);
        setIsProcessing(false);
        setProcessingStatus('');
      };
      img.src = finalCanvas.toDataURL('image/png');
    } catch (error) {
      console.error('Error processing signature:', error);
      setIsProcessing(false);
      setProcessingStatus('');
      alert('Error processing signature. Please try again.');
    }
  };

  const handleDocumentCropComplete = () => {
    // Always use perspective correction
    const img = documentPerspectiveImageRef.current;
    if (!img || perspectiveCorners.length !== 4) {
      console.error('Invalid image or corners', {
        img,
        corners: perspectiveCorners,
      });
      return;
    }

    try {
      const finalCanvas = applyPerspectiveTransform(img, perspectiveCorners);

      if (!finalCanvas || finalCanvas.width === 0 || finalCanvas.height === 0) {
        console.error('Invalid canvas generated');
        alert('Error processing document. Please try adjusting the corners.');
        return;
      }

      const newImg = new Image();
      newImg.onload = () => {
        setDocumentImage(newImg);
        setShowDocumentCropper(false);
      };
      newImg.onerror = () => {
        console.error('Failed to load transformed image');
        alert('Error loading transformed image. Please try again.');
      };
      newImg.src = finalCanvas.toDataURL('image/png');
    } catch (error) {
      console.error('Error in perspective transform:', error);
      alert('Error transforming document. Please try again.');
    }
  };

  const handleCancelSignatureCrop = () => {
    setShowSignatureCropper(false);
    setTempSignatureForCrop(null);
    if (signatureCropper) {
      signatureCropper.destroy();
      setSignatureCropper(null);
    }
    if (fileInputSignatureRef.current) {
      fileInputSignatureRef.current.value = '';
    }
  };

  const handleCancelDocumentCrop = () => {
    setShowDocumentCropper(false);
    setTempDocumentForCrop(null);
    if (documentCropper) {
      documentCropper.destroy();
      setDocumentCropper(null);
    }
    if (fileInputDocumentRef.current) {
      fileInputDocumentRef.current.value = '';
    }
  };

  // Initialize signature position when both images are ready
  useEffect(() => {
    if (!documentImage || !signatureImage) {
      return;
    }

    // Calculate initial signature position (centered)
    const aspectRatio = signatureImage.width / signatureImage.height;
    const initialWidth = Math.min(300, documentImage.width * 0.3);
    const initialHeight = initialWidth / aspectRatio;
    const x = (documentImage.width - initialWidth) / 2;
    const y = (documentImage.height - initialHeight) / 2;

    setSignatureTransform({
      x,
      y,
      width: initialWidth,
      height: initialHeight,
      rotation: 0,
    });
  }, [documentImage, signatureImage]);

  // Draw preview canvas with interactive controls
  const drawPreviewCanvas = useCallback(() => {
    if (!previewCanvasRef.current || !documentImage || !signatureImage) {
      return;
    }

    const canvas = previewCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size to A4 aspect ratio for consistent preview
    // Use a reasonable preview size (not full 300 DPI to save memory)
    const A4_ASPECT = 297 / 210; // height / width
    const previewWidth = 800;
    const previewHeight = Math.round(previewWidth * A4_ASPECT);

    canvas.width = previewWidth;
    canvas.height = previewHeight;

    // Draw document scaled to fit canvas
    const scale = Math.min(
      canvas.width / documentImage.width,
      canvas.height / documentImage.height
    );
    const scaledWidth = documentImage.width * scale;
    const scaledHeight = documentImage.height * scale;
    const offsetX = (canvas.width - scaledWidth) / 2;
    const offsetY = (canvas.height - scaledHeight) / 2;

    // Fill background
    ctx.fillStyle = '#f3f4f6';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw document centered
    ctx.drawImage(documentImage, offsetX, offsetY, scaledWidth, scaledHeight);

    // Scale signature coordinates to preview canvas
    const scaledSigX = offsetX + signatureTransform.x * scale;
    const scaledSigY = offsetY + signatureTransform.y * scale;
    const scaledSigWidth = signatureTransform.width * scale;
    const scaledSigHeight = signatureTransform.height * scale;

    // Draw signature with transform
    ctx.save();
    ctx.translate(
      scaledSigX + scaledSigWidth / 2,
      scaledSigY + scaledSigHeight / 2
    );
    ctx.rotate((signatureTransform.rotation * Math.PI) / 180);
    ctx.drawImage(
      signatureImage,
      -scaledSigWidth / 2,
      -scaledSigHeight / 2,
      scaledSigWidth,
      scaledSigHeight
    );
    ctx.restore();

    // Draw interactive controls (border, handles, rotation handle)
    ctx.save();
    ctx.translate(
      scaledSigX + scaledSigWidth / 2,
      scaledSigY + scaledSigHeight / 2
    );
    ctx.rotate((signatureTransform.rotation * Math.PI) / 180);

    // Draw border
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 2;
    ctx.strokeRect(
      -scaledSigWidth / 2,
      -scaledSigHeight / 2,
      scaledSigWidth,
      scaledSigHeight
    );

    // Draw corner handles
    const handleSize = 12;
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 2;

    const corners = [
      {
        x: -scaledSigWidth / 2,
        y: -scaledSigHeight / 2,
        cursor: 'nw-resize',
      }, // top-left
      {
        x: scaledSigWidth / 2,
        y: -scaledSigHeight / 2,
        cursor: 'ne-resize',
      }, // top-right
      {
        x: scaledSigWidth / 2,
        y: scaledSigHeight / 2,
        cursor: 'se-resize',
      }, // bottom-right
      {
        x: -scaledSigWidth / 2,
        y: scaledSigHeight / 2,
        cursor: 'sw-resize',
      }, // bottom-left
    ];

    corners.forEach((corner, index) => {
      ctx.fillRect(
        corner.x - handleSize / 2,
        corner.y - handleSize / 2,
        handleSize,
        handleSize
      );
      ctx.strokeRect(
        corner.x - handleSize / 2,
        corner.y - handleSize / 2,
        handleSize,
        handleSize
      );
    });

    // Draw rotation handle
    const rotationHandleY = -scaledSigHeight / 2 - 30;
    ctx.beginPath();
    ctx.moveTo(0, -scaledSigHeight / 2);
    ctx.lineTo(0, rotationHandleY);
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(0, rotationHandleY, 8, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.restore();
  }, [documentImage, signatureImage, signatureTransform]);

  // Redraw when transform changes
  useEffect(() => {
    drawPreviewCanvas();
  }, [drawPreviewCanvas]);

  // Helper function to get mouse position relative to canvas (in document coordinates)
  const getCanvasMousePos = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = previewCanvasRef.current;
    if (!canvas || !documentImage) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    // Mouse position in canvas pixels
    const canvasX = ((e.clientX - rect.left) / rect.width) * canvas.width;
    const canvasY = ((e.clientY - rect.top) / rect.height) * canvas.height;

    // Calculate the scale and offset used in drawing
    const scale = Math.min(
      canvas.width / documentImage.width,
      canvas.height / documentImage.height
    );
    const scaledWidth = documentImage.width * scale;
    const scaledHeight = documentImage.height * scale;
    const offsetX = (canvas.width - scaledWidth) / 2;
    const offsetY = (canvas.height - scaledHeight) / 2;

    // Convert from canvas coordinates to document coordinates
    const docX = (canvasX - offsetX) / scale;
    const docY = (canvasY - offsetY) / scale;

    return {
      x: docX,
      y: docY,
    };
  };

  // Detect which handle or area is being clicked
  const detectHandle = (mouseX: number, mouseY: number): string | null => {
    const cx = signatureTransform.x + signatureTransform.width / 2;
    const cy = signatureTransform.y + signatureTransform.height / 2;

    // Transform mouse coordinates to signature's local space
    const dx = mouseX - cx;
    const dy = mouseY - cy;
    const angle = (-signatureTransform.rotation * Math.PI) / 180;
    const localX = dx * Math.cos(angle) - dy * Math.sin(angle);
    const localY = dx * Math.sin(angle) + dy * Math.cos(angle);

    const hw = signatureTransform.width / 2;
    const hh = signatureTransform.height / 2;
    const handleSize = 12;
    const rotationHandleY = -hh - 30;

    // Check rotation handle
    if (Math.abs(localY - rotationHandleY) < 10 && Math.abs(localX) < 10) {
      return 'rotate';
    }

    // Check corner handles
    const corners = [
      { x: -hw, y: -hh, name: 'tl' },
      { x: hw, y: -hh, name: 'tr' },
      { x: hw, y: hh, name: 'br' },
      { x: -hw, y: hh, name: 'bl' },
    ];

    for (const corner of corners) {
      if (
        Math.abs(localX - corner.x) < handleSize &&
        Math.abs(localY - corner.y) < handleSize
      ) {
        return corner.name;
      }
    }

    // Check if inside signature bounds (for dragging)
    if (Math.abs(localX) < hw && Math.abs(localY) < hh) {
      return 'move';
    }

    return null;
  };

  // Mouse down handler
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = getCanvasMousePos(e);
    const handle = detectHandle(pos.x, pos.y);

    if (handle === 'rotate') {
      setIsRotating(true);
      setActiveHandle('rotate');
    } else if (handle && handle !== 'move') {
      setIsResizing(true);
      setActiveHandle(handle);
    } else if (handle === 'move') {
      setIsDragging(true);
      setActiveHandle('move');
    }

    setLastMousePos(pos);
  };

  // Mouse move handler
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!signatureImage || !documentImage) return;

    const pos = getCanvasMousePos(e);
    const dx = pos.x - lastMousePos.x;
    const dy = pos.y - lastMousePos.y;

    if (isDragging) {
      setSignatureTransform({
        ...signatureTransform,
        x: signatureTransform.x + dx,
        y: signatureTransform.y + dy,
      });
    } else if (isResizing && activeHandle) {
      // Calculate new size based on which corner is being dragged
      const angle = (-signatureTransform.rotation * Math.PI) / 180;
      const localDx = dx * Math.cos(angle) - dy * Math.sin(angle);

      const aspectRatio = signatureImage.width / signatureImage.height;

      let newWidth = signatureTransform.width;
      let newHeight = signatureTransform.height;

      if (activeHandle === 'br' || activeHandle === 'tr') {
        newWidth = signatureTransform.width + localDx * 2;
      } else if (activeHandle === 'bl' || activeHandle === 'tl') {
        newWidth = signatureTransform.width - localDx * 2;
      }

      newWidth = Math.max(50, Math.min(newWidth, documentImage.width));
      newHeight = newWidth / aspectRatio;

      setSignatureTransform({
        ...signatureTransform,
        width: newWidth,
        height: newHeight,
      });
    } else if (isRotating) {
      // Calculate rotation angle
      const cx = signatureTransform.x + signatureTransform.width / 2;
      const cy = signatureTransform.y + signatureTransform.height / 2;

      const angle = Math.atan2(pos.y - cy, pos.x - cx) * (180 / Math.PI) + 90;

      setSignatureTransform({
        ...signatureTransform,
        rotation: angle,
      });
    } else {
      // Update cursor based on what's under mouse
      const handle = detectHandle(pos.x, pos.y);
      const canvas = previewCanvasRef.current;
      if (canvas) {
        if (handle === 'rotate') {
          canvas.style.cursor = 'grab';
        } else if (handle === 'move') {
          canvas.style.cursor = 'move';
        } else if (handle) {
          canvas.style.cursor = 'nwse-resize';
        } else {
          canvas.style.cursor = 'default';
        }
      }
    }

    setLastMousePos(pos);
  };

  // Mouse up handler
  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
    setIsRotating(false);
    setActiveHandle(null);
  };

  // Composite the final image for download
  const compositeImage = useCallback(() => {
    if (!documentImage || !signatureImage || !compositeCanvasRef.current)
      return;

    const canvas = compositeCanvasRef.current;
    canvas.width = documentImage.width;
    canvas.height = documentImage.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Draw document as background
    ctx.drawImage(documentImage, 0, 0);

    // Draw signature with transform
    ctx.save();
    ctx.translate(
      signatureTransform.x + signatureTransform.width / 2,
      signatureTransform.y + signatureTransform.height / 2
    );
    ctx.rotate((signatureTransform.rotation * Math.PI) / 180);
    ctx.drawImage(
      signatureImage,
      -signatureTransform.width / 2,
      -signatureTransform.height / 2,
      signatureTransform.width,
      signatureTransform.height
    );
    ctx.restore();

    // Apply grayscale filter if enabled
    if (grayscaleOutput) {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      for (let i = 0; i < data.length; i += 4) {
        const gray =
          data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
        data[i] = gray; // Red
        data[i + 1] = gray; // Green
        data[i + 2] = gray; // Blue
      }
      ctx.putImageData(imageData, 0, 0);
    }
  }, [documentImage, signatureImage, signatureTransform, grayscaleOutput]);

  // Download signed document
  const handleDownload = () => {
    compositeImage();

    const canvas = compositeCanvasRef.current;
    if (!canvas) return;

    canvas.toBlob(blob => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'signed-document.png';
      a.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  };

  const handleClearSignature = () => {
    setSignatureImage(null);
    setSignatureTransform({
      x: 0,
      y: 0,
      width: 300,
      height: 150,
      rotation: 0,
    });
    if (fileInputSignatureRef.current) {
      fileInputSignatureRef.current.value = '';
    }
  };

  const handleClearDocument = () => {
    setDocumentImage(null);
    setSignatureTransform({
      x: 0,
      y: 0,
      width: 300,
      height: 150,
      rotation: 0,
    });
    if (fileInputDocumentRef.current) {
      fileInputDocumentRef.current.value = '';
    }
  };

  return (
    <ToolLayout
      title={`Signature placer | ${CONTACT_INFO.company.name}`}
      heroTitle="Signature placer"
      description="Crop your signature and document, then position the signature anywhere on the document with full control"
      canonicalUrl="https://niveshright.com/tools/signature-placer"
      backgroundColor={COLORS.mist}
      heroStyle="gradient"
      heroIcon={
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center"
          style={{ backgroundColor: `${COLORS.gold}20` }}
        >
          <PenTool size={32} style={{ color: COLORS.gold }} />
        </div>
      }
      showBreadcrumb={true}
      keywords="signature placer, digital signature, sign documents online, document signer, crop signature, remove background"
    >
      {/* Hidden canvas for compositing */}
      <canvas ref={compositeCanvasRef} style={{ display: 'none' }} />

      {/* Signature Cropper Modal */}
      {showSignatureCropper && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <motion.div
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Crop size={24} style={{ color: COLORS.accent }} />
                  <h3
                    className="text-xl font-bold ml-3"
                    style={{ color: COLORS.primary }}
                  >
                    Crop your signature
                  </h3>
                </div>
                <button
                  onClick={handleCancelSignatureCrop}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  disabled={isProcessing}
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    Crop your signature
                  </h4>
                  <div className="max-h-[50vh]">
                    {tempSignatureForCrop && (
                      <img
                        ref={signatureCropperImageRef}
                        src={tempSignatureForCrop}
                        alt="Crop preview"
                        style={{ maxWidth: '100%' }}
                      />
                    )}
                  </div>
                </div>

                {removeWhiteBg && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                      Preview (background removal)
                    </h4>
                    <div className="border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                      <img
                        ref={signaturePreviewCanvasRef}
                        className="w-full h-auto"
                        style={{ maxHeight: '50vh' }}
                        alt="Preview"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4 space-y-3">
                <Toggle
                  label="Remove white background"
                  checked={removeWhiteBg}
                  onChange={setRemoveWhiteBg}
                />

                {removeWhiteBg && (
                  <div className="pl-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sensitivity: {bgThreshold}
                    </label>
                    <input
                      type="range"
                      min="150"
                      max="240"
                      value={bgThreshold}
                      onChange={e => setBgThreshold(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((bgThreshold - 150) / (240 - 150)) * 100}%, #e5e7eb ${((bgThreshold - 150) / (240 - 150)) * 100}%, #e5e7eb 100%)`,
                      }}
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>More aggressive</span>
                      <span>More conservative</span>
                    </div>
                  </div>
                )}
              </div>

              {isProcessing && processingStatus && (
                <div className="mt-4 p-4 bg-blue-50 rounded-xl">
                  <div className="flex items-center">
                    <Loader className="animate-spin mr-3" size={20} />
                    <p className="text-sm font-medium text-gray-700">
                      {processingStatus}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3">
              <Button
                onClick={handleCancelSignatureCrop}
                variant="ghost"
                fullWidth
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSignatureCropComplete}
                variant="accent"
                fullWidth
                icon={Check}
                iconSize={20}
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Apply'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Document Cropper Modal */}
      {showDocumentCropper && (
        <DocumentCropperModal
          tempDocumentForCrop={tempDocumentForCrop}
          perspectiveCorners={perspectiveCorners}
          setPerspectiveCorners={setPerspectiveCorners}
          draggingCorner={draggingCorner}
          setDraggingCorner={setDraggingCorner}
          documentPerspectiveImageRef={documentPerspectiveImageRef}
          documentPerspectiveCanvasRef={documentPerspectiveCanvasRef}
          initializePerspectiveCorners={initializePerspectiveCorners}
          handleCancelDocumentCrop={handleCancelDocumentCrop}
          handleDocumentCropComplete={handleDocumentCropComplete}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Controls Section */}
        <div className="lg:col-span-1 space-y-6">
          {/* Upload Signature */}
          <motion.div
            className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="flex items-center mb-4">
              <PenTool size={20} style={{ color: COLORS.accent }} />
              <h3
                className="text-lg font-semibold ml-2"
                style={{ color: COLORS.primary }}
              >
                1. Upload signature
              </h3>
            </div>

            <input
              ref={fileInputSignatureRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              onChange={handleSignatureUpload}
              className="hidden"
              id="signature-upload"
            />

            {signatureImage ? (
              <div className="space-y-3">
                <div className="relative w-full h-32 bg-gray-50 rounded-xl flex items-center justify-center border-2 border-gray-200">
                  <img
                    src={signatureImage.src}
                    alt="Signature preview"
                    className="max-h-full max-w-full object-contain"
                  />
                  <button
                    onClick={handleClearSignature}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>
                <Button
                  onClick={() => fileInputSignatureRef.current?.click()}
                  variant="ghost"
                  fullWidth
                  icon={Upload}
                  iconSize={16}
                >
                  Change signature
                </Button>
              </div>
            ) : (
              <label
                htmlFor="signature-upload"
                className="block w-full p-8 border-2 border-dashed border-gray-300 rounded-xl text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all"
              >
                <Upload
                  size={32}
                  className="mx-auto mb-3"
                  style={{ color: COLORS.smoke }}
                />
                <p
                  className="text-sm font-medium"
                  style={{ color: COLORS.charcoal }}
                >
                  Click to upload signature
                </p>
                <p className="text-xs mt-1" style={{ color: COLORS.smoke }}>
                  PNG or JPEG
                </p>
              </label>
            )}
          </motion.div>

          {/* Upload Document */}
          <motion.div
            className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="flex items-center mb-4">
              <FileImage size={20} style={{ color: COLORS.sage }} />
              <h3
                className="text-lg font-semibold ml-2"
                style={{ color: COLORS.primary }}
              >
                2. Upload document
              </h3>
            </div>

            <input
              ref={fileInputDocumentRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              onChange={handleDocumentUpload}
              className="hidden"
              id="document-upload"
            />

            {documentImage ? (
              <div className="space-y-3">
                <div className="relative w-full h-32 bg-gray-50 rounded-xl flex items-center justify-center border-2 border-gray-200">
                  <img
                    src={documentImage.src}
                    alt="Document preview"
                    className="max-h-full max-w-full object-contain"
                  />
                  <button
                    onClick={handleClearDocument}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>
                <Button
                  onClick={() => fileInputDocumentRef.current?.click()}
                  variant="ghost"
                  fullWidth
                  icon={Upload}
                  iconSize={16}
                >
                  Change document
                </Button>
              </div>
            ) : (
              <label
                htmlFor="document-upload"
                className="block w-full p-8 border-2 border-dashed border-gray-300 rounded-xl text-center cursor-pointer hover:border-green-500 hover:bg-green-50 transition-all"
              >
                <FileImage
                  size={32}
                  className="mx-auto mb-3"
                  style={{ color: COLORS.smoke }}
                />
                <p
                  className="text-sm font-medium"
                  style={{ color: COLORS.charcoal }}
                >
                  Click to upload document
                </p>
                <p className="text-xs mt-1" style={{ color: COLORS.smoke }}>
                  PNG or JPEG
                </p>
              </label>
            )}
          </motion.div>

          {/* Download Options */}
          {signatureImage && documentImage && (
            <motion.div
              className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Toggle
                label="Grayscale output"
                checked={grayscaleOutput}
                onChange={setGrayscaleOutput}
                className="mb-4"
              />
              <Button
                onClick={handleDownload}
                variant="accent"
                fullWidth
                icon={Download}
                iconSize={20}
              >
                Download signed document
              </Button>
            </motion.div>
          )}
        </div>

        {/* Preview Section */}
        <motion.div
          className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h3
            className="text-lg font-semibold mb-4"
            style={{ color: COLORS.primary }}
          >
            3. Position signature on document
          </h3>

          {documentImage && signatureImage ? (
            <div>
              <div className="mb-4 border-2 border-gray-200 rounded-xl overflow-hidden bg-gray-50 flex items-center justify-center">
                <canvas
                  ref={previewCanvasRef}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  className="w-full h-auto"
                  style={{
                    maxWidth: '100%',
                    display: 'block',
                    cursor: 'default',
                  }}
                />
              </div>

              <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>✨ Interactive Controls:</strong>
                </p>
                <ul className="text-xs text-gray-600 space-y-1 ml-4">
                  <li>
                    • <strong>Move:</strong> Click and drag the signature
                  </li>
                  <li>
                    • <strong>Resize:</strong> Drag any corner handle
                  </li>
                  <li>
                    • <strong>Rotate:</strong> Drag the circular handle above
                    the signature
                  </li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center">
              <p className="text-gray-500">
                Upload and crop both signature and document to start positioning
              </p>
            </div>
          )}
        </motion.div>
      </div>

      {/* Educational Content */}
      <div className="mt-16 max-w-5xl mx-auto px-6">
        <article className="prose prose-lg md:prose-xl max-w-none">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6 md:mb-8">
            How to use the signature placer
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
            This tool helps you digitally sign documents without printing and
            scanning. Upload your signature and document, position it, and
            download the signed document.
          </p>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            How it works
          </h3>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mb-3 md:mb-4">
            Step 1: Upload and crop your signature
          </h4>
          <p className="text-lg text-gray-700 mb-4 md:mb-6">
            Upload a photo of your signature and crop it. The tool automatically
            removes the white background. Adjust the sensitivity slider if
            needed.
          </p>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mb-3 md:mb-4">
            Step 2: Upload and adjust your document
          </h4>
          <p className="text-lg text-gray-700 mb-4 md:mb-6">
            Upload your document. Drag the four corners to match the document
            edges - the tool will straighten it into a perfect rectangle.
          </p>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mb-3 md:mb-4">
            Step 3: Position and download
          </h4>
          <p className="text-lg text-gray-700 mb-6 md:mb-8">
            Drag, resize, and rotate your signature on the document preview.
            Click download when ready. All processing happens locally in your
            browser.
          </p>
        </article>
      </div>
    </ToolLayout>
  );
};
